import { h } from '@/lib/dom';
import { Button } from '@/components/Button';
import { SkeletonList } from '@/components/Skeleton';
import { toast } from '@/components/Toast';
import { confirmDialog, openModal } from '@/components/Modal';
import { data } from '@/lib/data';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { pageLayout } from './layout';
import { markdownSpan } from '@/lib/markdown-mini';
import { agendaRow } from './agenda';
import type { CallDetail } from '@/api/types';

// Format YYYY-MM-DD → «22 мая 2026»
function formatCallDateLong(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function overdueDays(iso: string): number {
  const today = new Date(todayIso());
  const callDate = new Date(iso);
  return Math.round((today.getTime() - callDate.getTime()) / 86400000);
}

function openDatePickerModal(
  opts: { title: string; initialDate?: string },
  onSave: (date: string) => Promise<void>,
): void {
  openModal({
    title: opts.title,
    size: 'sm',
    content: (close) => {
      const input = h('input', {
        id: 'call-date-input',
        type: 'date',
        class: 'form-input',
        value: opts.initialDate ?? '',
      }) as HTMLInputElement;

      const saveBtn = h('button', {
        class: 'btn btn-primary',
        type: 'button',
        on: {
          click: async () => {
            if (!input.value) return;
            close();
            await onSave(input.value);
          },
        },
      }, 'Сохранить') as HTMLButtonElement;

      const cancelBtn = h('button', {
        class: 'btn btn-secondary',
        type: 'button',
        on: { click: () => close() },
      }, 'Отмена');

      input.addEventListener('input', () => {
        saveBtn.disabled = !input.value;
      });
      saveBtn.disabled = !input.value;

      return h('div', null, [
        h('div', { class: 'form-field' }, [
          h('label', { class: 'form-label', for: 'call-date-input' }, 'Дата звонка'),
          input,
        ]),
        h('div', { class: 'modal-footer' }, [cancelBtn, saveBtn]),
      ]);
    },
  });
}

export async function renderCallDetail(ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }
  const isOwner = user.role === 'owner';
  const callId = parseInt(ctx.params['callId'] ?? '', 10);

  const contentEl = h('div', { class: 'agenda-call-page' });

  const root = pageLayout({
    user,
    active: 'agenda',
    children: [contentEl],
  });

  async function refresh(): Promise<void> {
    contentEl.innerHTML = '';
    contentEl.appendChild(SkeletonList(3));

    if (isNaN(callId)) {
      renderNotFound();
      return;
    }

    try {
      const call = await data.getCall(callId);
      contentEl.innerHTML = '';
      renderCall(call);
      document.dispatchEvent(new Event('route:settled'));
    } catch (err: unknown) {
      contentEl.innerHTML = '';
      const is404 = err instanceof Error && err.message === 'not found';
      if (is404) {
        renderNotFound();
      } else {
        contentEl.appendChild(h('div', { class: 'empty-state', role: 'alert' }, [
          h('p', null, 'Не удалось загрузить звонок.'),
          Button({ label: 'Повторить', variant: 'secondary', onClick: () => void refresh() }),
        ]));
      }
    }
  }

  function renderNotFound(): void {
    contentEl.appendChild(h('div', { class: 'empty-state' }, [
      h('p', null, 'Звонок не найден.'),
      h('a', {
        href: '/agenda',
        class: 'btn btn-ghost btn-sm',
        'aria-label': 'Вернуться к списку звонков',
      }, '← Все звонки'),
    ]));
  }

  function renderCall(call: CallDetail): void {
    const overdue = !call.is_archived && call.date < todayIso();
    const days = overdue ? overdueDays(call.date) : 0;

    // Header: back link
    const backLink = h('div', { class: 'agenda-call-back' }, [
      h('a', {
        href: '/agenda',
        class: 'btn btn-ghost btn-sm',
        'aria-label': 'Вернуться к списку звонков',
      }, '← Все звонки'),
    ]);

    // Title + meta badges
    const metaChildren: (HTMLElement | string)[] = [
      h('span', null, `${call.items_done} / ${call.items_total} тем`),
    ];
    if (call.is_archived) {
      metaChildren.push(h('span', { class: 'call-badge-archive' }, 'Архив'));
    } else if (overdue) {
      metaChildren.push(h('span', { class: 'call-overdue-badge' }, [
        h('span', { 'aria-hidden': 'true' }, '⚠'),
        ` Просрочен ${days} дн.`,
      ]));
    }

    const titles = h('div', { class: 'agenda-call-titles' }, [
      h('h1', { class: 'agenda-call-title' }, `Созвон ${formatCallDateLong(call.date)}`),
      h('div', { class: 'agenda-call-meta' }, metaChildren),
    ]);

    const header = h('div', { class: 'agenda-call-header' }, [backLink, titles]);

    // Owner controls
    if (isOwner) {
      const changeDateBtn = Button({
        label: 'Изменить дату',
        variant: 'secondary',
        size: 'sm',
        onClick: () => openDatePickerModal(
          { title: 'Изменить дату', initialDate: call.date },
          async (date) => {
            try {
              await data.updateCall(call.id, { date });
              toast('Дата обновлена', { type: 'success' });
              void refresh();
            } catch {
              toast('Не удалось обновить дату', { type: 'error' });
            }
          },
        ),
      });

      const deleteBtn = Button({
        label: 'Удалить звонок',
        variant: 'danger',
        size: 'sm',
        onClick: async () => {
          const ok = await confirmDialog(
            'Все темы звонка будут удалены. Это действие нельзя отменить.',
            'Удалить звонок',
          );
          if (!ok) return;
          try {
            await data.deleteCall(call.id);
            toast('Звонок удалён', { type: 'success' });
            navigate('/agenda');
          } catch {
            toast('Не удалось удалить звонок', { type: 'error' });
          }
        },
      });
      deleteBtn.classList.add('call-delete-btn');

      header.appendChild(h('div', { class: 'agenda-call-controls' }, [changeDateBtn, deleteBtn]));
    }

    contentEl.appendChild(header);

    // Items section
    contentEl.appendChild(h('h2', { class: 'agenda-call-section-heading' }, 'Темы'));

    const itemsContainer = h('div', { class: 'call-item-list', role: 'list' });

    const refreshItems = (): void => {
      void data.getCall(call.id).then((updated) => {
        itemsContainer.innerHTML = '';
        renderItemsList(updated);
      });
    };

    const renderItemsList = (detail: CallDetail): void => {
      if (isOwner) {
        // Add row
        let addInput: HTMLInputElement;
        const handleAdd = async (): Promise<void> => {
          const body = addInput.value.trim();
          if (!body) return;
          const saved = addInput.value;
          addInput.value = '';
          try {
            await data.createCallItem(detail.id, { body });
            refreshItems();
          } catch {
            addInput.value = saved;
            toast('Не удалось добавить тему', { type: 'error' });
          }
        };
        const addRow = h('div', { class: 'agenda-add-row' }, [
          (addInput = h('input', {
            class: 'form-input agenda-add-input',
            type: 'text',
            placeholder: 'Новая тема…',
            'aria-label': 'Новая тема',
            on: {
              keydown: (ev: KeyboardEvent) => {
                if (ev.key === 'Enter') void handleAdd();
              },
            },
          }) as HTMLInputElement),
          Button({
            label: '+ Добавить',
            variant: 'primary',
            size: 'sm',
            onClick: () => void handleAdd(),
          }),
        ]);
        itemsContainer.appendChild(addRow);
        itemsContainer.appendChild(h('p', { class: 'agenda-add-hint' }, 'Можно: **жирный**, *курсив*, [ссылка](url)'));
      }

      const sorted = [...detail.items].sort((a, b) => a.sort_order - b.sort_order);
      if (sorted.length === 0) {
        const emptyText = isOwner ? 'Тем к звонку пока нет' : 'Тем к звонку нет';
        itemsContainer.appendChild(h('div', { class: 'empty-state' }, emptyText));
      } else {
        sorted.forEach((item, idx) => {
          itemsContainer.appendChild(agendaRow(item, sorted, idx, isOwner, refreshItems));
        });
      }
    };

    renderItemsList(call);
    contentEl.appendChild(itemsContainer);

    // Notes section
    contentEl.appendChild(h('h2', { class: 'agenda-call-section-heading' }, 'Заметки'));

    if (isOwner) {
      const notesArea = h('div', { class: 'call-notes-area' });
      const textarea = h('textarea', {
        id: 'call-notes-textarea',
        class: 'call-notes form-textarea',
        rows: '6',
        placeholder: 'Заметки после созвона (markdown)…',
        'aria-label': 'Заметки к звонку',
        'aria-describedby': 'call-notes-status',
      }, call.notes ?? '') as HTMLTextAreaElement;

      const statusEl = h('div', {
        id: 'call-notes-status',
        class: 'call-notes-status',
        role: 'status',
        'aria-live': 'polite',
      });

      let debounceTimer: ReturnType<typeof setTimeout> | null = null;
      let savedTimer: ReturnType<typeof setTimeout> | null = null;

      textarea.addEventListener('input', () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        statusEl.textContent = '';
        statusEl.className = 'call-notes-status';
        debounceTimer = setTimeout(() => void autosave(), 800);
      });

      const autosave = async (): Promise<void> => {
        statusEl.textContent = 'Сохранение…';
        statusEl.className = 'call-notes-status call-notes-status--saving';
        try {
          await data.updateCall(call.id, { notes: textarea.value || null });
          statusEl.textContent = '✓ Сохранено';
          statusEl.className = 'call-notes-status call-notes-status--saved';
          if (savedTimer) clearTimeout(savedTimer);
          savedTimer = setTimeout(() => {
            statusEl.textContent = '';
            statusEl.className = 'call-notes-status';
          }, 3000);
        } catch {
          statusEl.textContent = '⚠ Не сохранено';
          statusEl.className = 'call-notes-status call-notes-status--error';
        }
      };

      notesArea.append(textarea, statusEl);
      contentEl.appendChild(notesArea);
    } else {
      // Doctor: read-only
      const readonlyEl = h('div', { class: 'call-notes-readonly' });
      if (call.notes) {
        readonlyEl.append(...Array.from(markdownSpan(call.notes).childNodes));
      } else {
        readonlyEl.appendChild(h('p', { class: 'text-tertiary' }, 'Заметок к звонку нет'));
      }
      contentEl.appendChild(readonlyEl);
    }
  }

  void refresh();
  return root;
}
